/**
 * Authentication Context for managing user authentication state
 * Microservice-compatible token-based authentication
 *
 * Portal authentication state machine (deterministic):
 * - unauthenticated: No IdP token; user not signed in
 * - azure_authenticated: IdP (e.g. MSAL) has token; portal has not validated yet
 * - portal_validating: Portal backend validation in progress
 * - portal_authenticated: Portal validated; user and token set; full access
 * - portal_error: Validation failed or error (e.g. login_error)
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { AuthContextType, PortalAuthState, User } from './authContext.types';
import { AuthContext } from './authReactContext';
import { startSession, endSession } from '../services/session.service';
import { SessionPolicyLogoutRegistrar } from '../components/SessionPolicyLogoutRegistrar/SessionPolicyLogoutRegistrar';
import { CrossPortalLogoutListener } from '../components/CrossPortalLogoutListener/CrossPortalLogoutListener';
import { Navigate } from 'react-router-dom';
import { clearAllLocalStorage, clearAllSessionStorage } from '../utils/localStorage.utils';
import {
  setSecureJSON,
  getSecureJSON,
  removeSecureItem,
  clearAllSecureStorage,
  markPriorPortalAuthentication,
  clearPriorPortalAuthentication,
} from '@hsus-dev/session-policy';
import { clearRegistrationData } from '../utils/secureRegistrationStorage.utils';
import { refreshToken as refreshTokenApi, logout as logoutApi, getAuthProvider } from '../services/authApi.service';
import { tokenService } from '../services/token.service';
import { MsalAuthProvider } from '../services/msalAuthProvider.service';
import { 
  TOKEN_REFRESH_BUFFER_MS, 
  DEFAULT_TOKEN_REFRESH_TIME_MS, 
  TOKEN_REFRESH_PERCENTAGE,
  MIN_REFRESH_WINDOW_MS,
} from '../config/time.config';
import { tokenRetryGuard } from '../utils/tokenRetryGuard.utils';
import { AUTH_API_CONFIG } from '../config/api.config';
import { msalReady } from '../services/msalBootstrap.service';
import { getAuthScopes } from '../config/msal.config';
import { isUserEmailValid } from '../utils/entraAccount.utils';
import { usesRefreshTokenApi, createProviderContext } from '../types/auth.types';
import { acquireTokenSilentDeduplicated } from '../utils/tokenAcquisition.utils';
import { setIsLoggingInGetter, setupApiInterceptor } from '../services/api.service';
import { getValidAccessToken } from '../utils/tokenCache.utils';
import type { UserProfile } from '../validation/userProfileSchemas';
import {
  clearPortalPersistedAuthPartial,
  performPortalAuthTeardown,
} from '../services/portalLogout.service';
import { refreshRolesCache } from '../utils/rolesCache.utils';
import { completeLogin } from '../utils/completeLogin.utils';
import { clearMsalCacheSynchronously } from '../utils/msalCache.utils';
import { invalidateProfileCache, isEntraObjectId } from '../utils/userProfileCache.utils';
import { getSharedAccessToken, decodeJwtPayload, getEntraIdFromToken } from '../auth/sharedAuth';
import { UserProfileApi } from '../api/userProfile';
import { normalizeProfileRoles } from '../utils/profileRoles.utils';
import { setCachedProfile } from '../utils/userProfileCache.utils';

import { AUTH_USER_KEY, AUTH_USER_PROFILE_KEY } from '../constants/storageKeyConstants';

/** Allowed state transitions: from -> to[]. Invalid transitions are logged and ignored. */
const ALLOWED_TRANSITIONS: Record<PortalAuthState, PortalAuthState[]> = {
  unauthenticated: ['unauthenticated', 'azure_authenticated', 'portal_validating', 'portal_authenticated', 'portal_error'],
  azure_authenticated: ['portal_validating', 'portal_error', 'unauthenticated'],
  portal_validating: ['portal_authenticated', 'portal_error', 'unauthenticated'],
  portal_authenticated: ['unauthenticated'],
  portal_error: ['unauthenticated', 'portal_validating'],
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthStateRaw] = useState<PortalAuthState>('unauthenticated');
  const authStateRef = useRef<PortalAuthState>(authState);
  useEffect(() => {
    authStateRef.current = authState;
  }, [authState]);

  const setAuthState = useCallback((next: PortalAuthState) => {
    const current = authStateRef.current;
    const allowed = ALLOWED_TRANSITIONS[current];
    if (!allowed?.includes(next)) {
      if (import.meta.env.DEV) {
        console.warn(`[AuthContext] Invalid transition ignored: ${current} -> ${next}. Allowed from ${current}:`, allowed);
      }
      return;
    }
    setAuthStateRaw(next);
  }, []);

  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshTokenValue, setRefreshTokenValue] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Refs to track current values without causing dependency issues
  const tokenRef = useRef<string | null>(null);
  const userRef = useRef<User | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Clear refresh timer
  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Schedule automatic token refresh using percentage-based timing
  // Note: Dependencies are minimal because:
  // - State setters (setToken, setRefreshTokenValue, setUser) are stable from useState
  // - tokenService, tokenRetryGuard, AUTH_API_CONFIG are imported singletons/constants
  // - getAuthProvider, refreshTokenApi are imported functions (stable)
  const scheduleTokenRefresh = useCallback((accessToken: string, expiresIn?: number) => {
    clearRefreshTimer();

    if (!accessToken) return;

    // Calculate refresh time using percentage-based approach
    let refreshTime: number;
    
    if (expiresIn) {
      // expiresIn is in seconds, convert to milliseconds
      const tokenLifetimeMs = expiresIn * 1000;
      
      // Use percentage-based refresh (default: 82% of lifetime elapsed)
      // This adapts better to short-lived tokens (e.g., 10-15 minutes)
      const refreshPercentage = TOKEN_REFRESH_PERCENTAGE;
      refreshTime = tokenLifetimeMs * refreshPercentage;
      
      // Ensure we don't refresh too close to expiration (minimum window)
      const timeUntilExpiration = tokenLifetimeMs - refreshTime;
      if (timeUntilExpiration < MIN_REFRESH_WINDOW_MS) {
        // If calculated refresh time is too close to expiration, use minimum window
        refreshTime = tokenLifetimeMs - MIN_REFRESH_WINDOW_MS;
      }
    } else {
      // Fallback: parse token expiration
      const expirationTime = tokenService.getTokenExpirationTime(accessToken);
      if (expirationTime) {
        const now = Date.now();
        const tokenLifetimeMs = expirationTime - now;
        
        if (tokenLifetimeMs > 0) {
          // Use percentage-based refresh
          const refreshPercentage = TOKEN_REFRESH_PERCENTAGE;
          refreshTime = tokenLifetimeMs * refreshPercentage;
          
          // Ensure minimum window
          const timeUntilExpiration = tokenLifetimeMs - refreshTime;
          if (timeUntilExpiration < MIN_REFRESH_WINDOW_MS) {
            refreshTime = tokenLifetimeMs - MIN_REFRESH_WINDOW_MS;
          }
        } else {
          // Token already expired, use legacy fixed offset as fallback
          refreshTime = expirationTime - now - TOKEN_REFRESH_BUFFER_MS;
        }
      } else {
        // No expiration info available, use default
        refreshTime = DEFAULT_TOKEN_REFRESH_TIME_MS;
      }
    }

    // Only schedule if refresh time is positive and reasonable
    if (refreshTime > 0 && refreshTime < 24 * 60 * 60 * 1000) { // Max 24 hours
      refreshTimerRef.current = setTimeout(async () => {
        // Check retry guard before attempting refresh
        if (!tokenRetryGuard.shouldRetry()) {
          console.error('[AuthContext] Token refresh blocked by retry guard - circuit open');
          setAuthState('unauthenticated');
          setToken(null);
          setRefreshTokenValue(null);
          setUser(null);
          setUserProfile(null);
          clearRefreshTimer();
          await tokenService.removeTokens().catch(() => {});
          window.dispatchEvent(new CustomEvent('auth:logout'));
          return;
        }

        try {
          // PHASE 5: Skip timer-based refresh for MSAL
          // MSAL handles its own token refresh via acquireTokenSilent()
          // Timer-based refresh is only for REST/OAuth2 providers
          if (AUTH_API_CONFIG.providerType === 'msal') {
            // MSAL doesn't use timer-based refresh - tokens are refreshed on-demand via acquireTokenSilent()
            // This timer should not have been scheduled for MSAL in the first place
            console.warn('[AuthContext] Timer-based refresh triggered for MSAL provider. This should not happen.');
            clearRefreshTimer();
            return;
          }
          
          // For non-MSAL providers, use refresh token
          // HARD GUARD: Prevent refreshTokenApi() calls for MSAL provider
          const providerContext = createProviderContext(
            await tokenService.getRefreshToken(),
            await msalReady
          );
          
          if (!usesRefreshTokenApi(providerContext)) {
            // This should never happen if guards are correct, but fail fast if it does
            throw new Error(
              '[AuthContext] CRITICAL: refreshTokenApi() called for MSAL provider. ' +
              'MSAL uses acquireTokenSilent(), not refreshTokenApi(). ' +
              'This is a programming error.'
            );
          }
          
          const currentRefreshToken = providerContext.refreshToken;
          if (currentRefreshToken) {
            const response = await refreshTokenApi(currentRefreshToken);
            const { accessToken: newAccessToken, refreshToken: newRefreshToken, expiresIn: newExpiresIn } = response;
            
            // Record success in retry guard
            tokenRetryGuard.recordSuccess();
            
            setToken(newAccessToken);
            await tokenService.setAccessToken(newAccessToken);
            
            if (newRefreshToken) {
              setRefreshTokenValue(newRefreshToken);
              await tokenService.setRefreshToken(newRefreshToken);
            }
            
            // Schedule next refresh
            scheduleTokenRefresh(newAccessToken, newExpiresIn);
          } else {
            throw new Error('No refresh token available');
          }
        } catch (error) {
          console.error('[AuthContext] Automatic token refresh failed:', error);
          
          // Record failure in retry guard
          tokenRetryGuard.recordFailure();
          
          // Determine error type and handle accordingly
          const isInteractionRequired = error instanceof Error && 
            (error.message.includes('user interaction') || 
             error.message.includes('interaction_required') ||
             error.message.includes('consent_required') ||
             error.message.includes('login_required'));
          
          if (isInteractionRequired) {
            console.warn('[AuthContext] Token refresh requires user interaction. Session cleared.');
            setAuthState('unauthenticated');
            setToken(null);
            setRefreshTokenValue(null);
            setUser(null);
            clearRefreshTimer();
            await tokenService.removeTokens().catch(() => {});
            window.dispatchEvent(new CustomEvent('auth:logout'));
          } else if (tokenRetryGuard.isCircuitOpen()) {
            console.error('[AuthContext] Token refresh circuit breaker opened. Too many consecutive failures.');
            setAuthState('unauthenticated');
            setToken(null);
            setRefreshTokenValue(null);
            setUser(null);
            clearRefreshTimer();
            await tokenService.removeTokens().catch(() => {});
            window.dispatchEvent(new CustomEvent('auth:logout'));
          } else {
            // Other error - check if we should retry with backoff
            const backoffDelay = tokenRetryGuard.getBackoffDelay();
            if (backoffDelay > 0 && tokenRetryGuard.shouldRetry()) {
              console.warn(`[AuthContext] Retrying token refresh after ${backoffDelay}ms backoff`);
              setTimeout(() => {
                // Retry once more with backoff
                scheduleTokenRefresh(accessToken, expiresIn);
              }, backoffDelay);
            } else {
              console.error('[AuthContext] Token refresh failed after retries. Logging out.');
              setAuthState('unauthenticated');
              setToken(null);
              setRefreshTokenValue(null);
              setUser(null);
              clearRefreshTimer();
              await tokenService.removeTokens().catch(() => {});
              window.dispatchEvent(new CustomEvent('auth:logout'));
            }
          }
        }
      }, refreshTime);
    }
  }, [clearRefreshTimer]);

  // Helper function to silently logout and redirect to error page
  const silentLogoutAndRedirect = useCallback(async () => {
    console.warn('[AuthContext] Storage unavailable - silently logging out and redirecting to /error');

    setAuthState('unauthenticated');
    setToken(null);
    setRefreshTokenValue(null);
    setUser(null);
    setUserProfile(null);
    clearRefreshTimer();
    
    try {
      // Try to clear storage (may fail if storage is unavailable)
      await tokenService.removeTokens().catch(() => {});
      removeSecureItem(AUTH_USER_KEY);
      clearAllSecureStorage();
      clearRegistrationData();
      clearAllSessionStorage();
      clearPriorPortalAuthentication();
      clearAllLocalStorage();
    } catch (error) {
      // Ignore errors when clearing storage - it may already be cleared
      console.debug('[AuthContext] Error clearing storage (expected if storage unavailable):', error);
    }

    if (window.location.pathname !== '/error' && window.location.pathname !== '/signin') {
      window.location.href = '/error';
    }
  }, [clearRefreshTimer]);

  // Initialize auth state from token service
  // Use a ref to track if we've already initialized to prevent infinite loops
  const hasInitializedRef = useRef(false);
  
  useEffect(() => {
    // Only initialize once on mount, or when isLoggingIn changes from true to false
    // Don't depend on token/user as they are what we're setting - this prevents infinite loops
    if (hasInitializedRef.current && !isLoggingIn) {
      return; // Already initialized and not in login process
    }

    // Context7 pattern: prevent setState after unmount or when effect re-runs (see CONTEXT7_REVIEW_MSAL_RESTORE_VALIDATION.md)
    let cancelled = false;

    const initializeAuth = async () => {
      try {
        // If we're currently logging in, skip initialization (login will handle setting auth state)
        if (isLoggingIn) {
          setIsLoading(false);
          hasInitializedRef.current = true;
          return;
        }

        let storedToken: string | null = null;
        let storedRefreshToken: string | null = null;
        let storedUser: User | null = null;
        
        try {
          storedToken = await tokenService.getAccessToken();
        } catch (error) {
          // Handle decryption errors - token data is corrupted
          if (error instanceof Error && error.message === 'DECRYPTION_FAILED_CORRUPTED_DATA') {
            console.warn('[AuthContext] Token data is corrupted (decryption failed) - corrupted data should be cleared');
            // If we have auth state in memory, this is a problem - logout
            // Otherwise, just clear the corrupted data and continue (user is logging in)
            // Use refs to get current values without causing dependency issues
            if (tokenRef.current || userRef.current) {
              console.warn('[AuthContext] Auth state exists but token is corrupted - silently logging out');
              await silentLogoutAndRedirect();
              if (cancelled) return;
              setIsLoading(false);
              hasInitializedRef.current = true;
              return;
            }
            // No auth state in memory - corrupted data already cleared by getSecureItem
            // Just continue initialization (storedToken will be null)
            storedToken = null; // Ensure it's null
          } else {
            // For other errors, log but continue (might be a temporary issue)
            console.error('[AuthContext] Failed to retrieve token:', error);
            storedToken = null;
          }
        }
        
        try {
          storedRefreshToken = await tokenService.getRefreshToken();
        } catch (error) {
          // Handle decryption errors - refresh token data is corrupted
          if (error instanceof Error && error.message === 'DECRYPTION_FAILED_CORRUPTED_DATA') {
            console.warn('[AuthContext] Refresh token data is corrupted (decryption failed) - cleared corrupted data');
            // If we have auth state in memory, this is a problem - logout
            // Otherwise, just clear the corrupted data and continue
            // Use refs to get current values without causing dependency issues
            if (tokenRef.current || userRef.current) {
              console.warn('[AuthContext] Auth state exists but refresh token is corrupted - silently logging out');
              await silentLogoutAndRedirect();
              if (cancelled) return;
              setIsLoading(false);
              hasInitializedRef.current = true;
              return;
            }
            // No auth state in memory - just continue (corrupted data already cleared)
          } else {
            console.error('[AuthContext] Failed to retrieve refresh token:', error);
          }
        }
        
        let storedUserProfile: UserProfile | null = null;
        try {
          storedUser = await getSecureJSON<User>(AUTH_USER_KEY);
          // Try to restore userProfile if available
          try {
            storedUserProfile = await getSecureJSON<UserProfile>(AUTH_USER_PROFILE_KEY);
          } catch (profileError) {
            console.warn('[AuthContext] Failed to retrieve user profile (optional):', profileError);
          }
        } catch (error) {
          // Handle decryption errors - user data is corrupted
          if (error instanceof Error && error.message === 'DECRYPTION_FAILED_CORRUPTED_DATA') {
            console.warn('[AuthContext] User data is corrupted (decryption failed) - cleared corrupted data');
            // If we have auth state in memory, this is a problem - logout
            // Otherwise, just clear the corrupted data and continue
            // Use refs to get current values without causing dependency issues
            if (tokenRef.current || userRef.current) {
              console.warn('[AuthContext] Auth state exists but user data is corrupted - silently logging out');
              await silentLogoutAndRedirect();
              if (cancelled) return;
              setIsLoading(false);
              hasInitializedRef.current = true;
              return;
            }
            // No auth state in memory - just continue (corrupted data already cleared)
          } else {
            // For other errors, log and continue (storedUser will be null)
            console.error('[AuthContext] Failed to retrieve user data:', error);
          }
        }

        // Validate stored user email - if it's a GUID (not email-like), treat as invalid
        // This fixes cases where old stored data has GUID emails from before the fix
        if (storedUser && !isUserEmailValid(storedUser)) {
          // Clear stored user data so we re-extract from MSAL account
          storedUser = null;
          // Also clear the stored user data from storage
          try {
            removeSecureItem(AUTH_USER_KEY);
          } catch (clearError) {
            console.warn('[AuthContext] Error clearing invalid stored user data:', clearError);
          }
        }

        if (storedToken && storedUser) {
          try {
            // Check if token is expired using token service
            if (tokenService.isTokenExpired(storedToken)) {
              // HARD GUARD: Only use refreshTokenApi() for REST/OAuth2 providers
              // MSAL provider should use acquireTokenSilent() (handled in MSAL restore path above)
              const providerContext = createProviderContext(
                storedRefreshToken,
                await msalReady
              );
              if (cancelled) return;

              if (usesRefreshTokenApi(providerContext) && storedRefreshToken) {
                try {
                  const response = await refreshTokenApi(storedRefreshToken);
                  if (cancelled) return;
                  const { accessToken, refreshToken: newRefreshToken, expiresIn } = response;
                  setAuthState('portal_authenticated');
                  setToken(accessToken);
                  setRefreshTokenValue(newRefreshToken || storedRefreshToken);
                  setUser(storedUser);
                  setUserProfile(storedUserProfile);
                  await tokenService.setAccessToken(accessToken);
                  if (newRefreshToken) {
                    await tokenService.setRefreshToken(newRefreshToken);
                  }
                  scheduleTokenRefresh(accessToken, expiresIn);
                } catch (error) {
                  console.error('Token refresh failed on initialization:', error);
                  // Clear invalid tokens
                  await tokenService.removeTokens();
                  if (cancelled) return;
                  removeSecureItem(AUTH_USER_KEY);
                } finally {
                  if (!cancelled) {
                    setIsLoading(false);
                    hasInitializedRef.current = true;
                  }
                }
                return;
              } else if (AUTH_API_CONFIG.providerType === 'msal') {
                console.warn('[AuthContext] MSAL token expired and MSAL restore failed. Clearing auth state.');
                setAuthState('unauthenticated');
                await tokenService.removeTokens();
                if (cancelled) return;
                removeSecureItem(AUTH_USER_KEY);
                setIsLoading(false);
                hasInitializedRef.current = true;
                return;
              } else {
                // Token expired and no refresh token - clear auth
                await tokenService.removeTokens();
                removeSecureItem(AUTH_USER_KEY);
              }
            } else {
              // For MSAL: ensure instance is ready so API interceptor can get token on first request after refresh
              if (AUTH_API_CONFIG.providerType === 'msal') {
                await msalReady;
                if (cancelled) return;
              }
              setAuthState('portal_authenticated');
              setToken(prev => prev !== storedToken ? storedToken : prev);
              if (AUTH_API_CONFIG.providerType !== 'msal') {
                setRefreshTokenValue(prev => prev !== storedRefreshToken ? storedRefreshToken : prev);
              } else {
                setRefreshTokenValue(null);
              }
              setUser(prev => prev !== storedUser ? storedUser : prev);
              setUserProfile(storedUserProfile);
              if (AUTH_API_CONFIG.providerType !== 'msal') {
                scheduleTokenRefresh(storedToken);
              }
              // Fetch RBAC roles (GET .../rbac/roles) so permissions apply to buttons/links
              if (!cancelled) {
                refreshRolesCache().catch((err) => {
                  if (import.meta.env.DEV) console.warn('[AuthContext] RBAC roles refresh on restore failed:', err);
                });
              }
            }
          } catch (error) {
            console.error('Failed to parse user data:', error);
            await tokenService.removeTokens();
            removeSecureItem(AUTH_USER_KEY);
          }
        } else {
          // No tokenService session; try shared token from host portal redirect (URL hash/postMessage)
          const sharedToken = getSharedAccessToken();
          if (sharedToken && !tokenService.isTokenExpired(sharedToken)) {
            const payload = decodeJwtPayload(sharedToken);
            if (payload) {
              const entraId = getEntraIdFromToken(sharedToken);
              const id = payload.oid || payload.sub || 'shared';
              const email = payload.email || payload.sub || '';
              const name = payload.name || email || 'User';
              let userToSet: User = { id, email, name };
              let userProfileToSet: UserProfile | null = null;

              // Fetch user profile by Entra ID so we have roles for RBAC (ActiveRoleContext + PermissionsContext)
              // Use a timeout so we never hang indefinitely (e.g. network/API down) — then show app with minimal user
              const PROFILE_FETCH_TIMEOUT_MS = 15_000;
              if (entraId) {
                try {
                  const fetchProfilePromise = UserProfileApi.getByEntraObjectId(entraId);
                  const timeoutPromise = new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error('Profile fetch timeout')), PROFILE_FETCH_TIMEOUT_MS)
                  );
                  const profile = await Promise.race([fetchProfilePromise, timeoutPromise]);
                  if (profile && !cancelled) {
                    await setCachedProfile(entraId, profile);
                    const normalizedRoles = normalizeProfileRoles(profile.roles);
                    userProfileToSet = profile;
                    userToSet = {
                      id: entraId,
                      profileUserId: profile.userId != null ? String(profile.userId) : undefined,
                      email: profile.emailAddress ?? email,
                      name:
                        profile.firstName && profile.lastName
                          ? `${profile.firstName} ${profile.lastName}`.trim()
                          : profile.userName ?? name,
                      roles: normalizedRoles,
                      role: normalizedRoles.length > 0 ? normalizedRoles[0] : undefined,
                    };
                  }
                } catch (profileErr) {
                  if (import.meta.env.DEV) {
                    console.warn('[AuthContext] Shared-auth profile fetch failed, using minimal user:', profileErr);
                  }
                }
                if (!cancelled) {
                  refreshRolesCache().catch((err) => {
                    if (import.meta.env.DEV) console.warn('[AuthContext] RBAC roles refresh failed:', err);
                  });
                }
              }

              if (!cancelled) {
                setAuthState('portal_authenticated');
                setToken(sharedToken);
                setUser(userToSet);
                setUserProfile(userProfileToSet);
                setRefreshTokenValue(null);
                setIsLoading(false);
                hasInitializedRef.current = true;
              }
              return;
            }
          }
        }
        
        /**
         * IMPORTANT:
         * If a previous login attempt failed (login_error is set),
         * we MUST NOT restore auth state from MSAL cache.
         *
         * MSAL cache validity does NOT imply backend validation success.
         * Skipping restore here prevents half-authenticated UI states.
         */
        const hasLoginError = sessionStorage.getItem('login_error') !== null;
        if (hasLoginError) {
          if (cancelled) return;
          setAuthState('portal_error');
          setToken(null);
          setRefreshTokenValue(null);
          setUser(null);
          setUserProfile(null);
          setIsLoading(false);
          hasInitializedRef.current = true;
          return;
        }
        
        // ALWAYS attempt MSAL restore first (MSAL is source of truth)
        // Only fall back to stored tokens if MSAL truly has nothing
        if (AUTH_API_CONFIG.providerType === 'msal') {
          try {
            // Wait for MSAL bootstrap to complete (no retries/sleeps needed)
            const msalInstance = await msalReady;
            if (cancelled) return;

            if (msalInstance) {
              // Get active account (guaranteed to be set after msalReady)
              const account = msalInstance.getActiveAccount();
              
              if (account) {
                try {
                  // Acquire fresh token from MSAL (validates session is still valid)
                  const scopes = getAuthScopes();
                  const response = await acquireTokenSilentDeduplicated(
                    msalInstance,
                    account,
                    scopes
                  );
                  if (cancelled) return;

                  // Do NOT unlock the app yet: set validating and await UserService
                  await tokenService.setAccessToken(response.accessToken);
                  if (cancelled) return;
                  setAuthState('portal_validating');

                  const loginResult = await completeLogin(response.accessToken, account);
                  if (cancelled) return;

                  if (loginResult.success && loginResult.user) {
                    setAuthState('portal_authenticated');
                    setToken(response.accessToken);
                    setRefreshTokenValue(null);
                    setUser(loginResult.user);
                    setUserProfile(loginResult.userProfile ?? null);
                    await setSecureJSON(AUTH_USER_KEY, loginResult.user);
                    if (loginResult.userProfile) {
                      await setSecureJSON(AUTH_USER_PROFILE_KEY, loginResult.userProfile);
                    }
                    return;
                  }

                  // Validation failed: clear MSAL active account and set portal_error
                  clearMsalCacheSynchronously(msalInstance, account);
                  setAuthState('portal_error');
                  setToken(null);
                  setRefreshTokenValue(null);
                  setUser(null);
                  setUserProfile(null);
                  try {
                    removeSecureItem(AUTH_USER_KEY);
                    removeSecureItem(AUTH_USER_PROFILE_KEY);
                  } catch {
                    // ignore
                  }
                  if (loginResult.error) {
                    sessionStorage.setItem('msal_auth_error', JSON.stringify(loginResult.error));
                    sessionStorage.setItem('login_error', 'true');
                  }
                  return;
                } catch (silentError) {
                  console.warn('[AuthContext] MSAL silent token acquisition failed, falling back to stored tokens:', silentError);
                }
              }
            }
          } catch (error) {
            console.warn('[AuthContext] MSAL SSO restore failed, falling back to stored tokens:', error);
          }
        }
        
        // Only use stored tokens if MSAL restore failed or MSAL has no account
        // (This is the fallback, not the primary path)
      } catch (error) {
        console.error('Failed to initialize auth:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          hasInitializedRef.current = true;
        }
      }
    };

    void initializeAuth();

    return () => {
      cancelled = true;
    };
  }, [scheduleTokenRefresh, isLoggingIn, silentLogoutAndRedirect]);

  // Ensure permissions are driven by User API (GET /api/v1/users/entra/{entraId}) — Form Engine Portal convention.
  // When user has Entra ID but no roles (e.g. restored from old storage), fetch profile and update so RBAC applies to buttons/links.
  const rolesRefreshAttemptedRef = useRef<string | null>(null);
  useEffect(() => {
    if (authState !== 'portal_authenticated' || !user?.id) {
      rolesRefreshAttemptedRef.current = null;
      return;
    }
    if (!isEntraObjectId(user.id)) return;
    if (user.roles && user.roles.length > 0) return;
    if (rolesRefreshAttemptedRef.current === user.id) return;
    rolesRefreshAttemptedRef.current = user.id;

    let cancelled = false;
    (async () => {
      try {
        const profile = await UserProfileApi.getByEntraObjectId(user.id);
        if (cancelled || !profile) return;
        await setCachedProfile(user.id, profile);
        const normalizedRoles = normalizeProfileRoles(profile.roles);
        const updatedUser: User = {
          ...user,
          profileUserId: profile.userId != null ? String(profile.userId) : undefined,
          email: profile.emailAddress ?? user.email,
          name:
            profile.firstName && profile.lastName
              ? `${profile.firstName} ${profile.lastName}`.trim()
              : profile.userName ?? user.name,
          roles: normalizedRoles,
          role: normalizedRoles.length > 0 ? normalizedRoles[0] : undefined,
        };
        setUser(updatedUser);
        setUserProfile(profile);
        await setSecureJSON(AUTH_USER_KEY, updatedUser);
        await setSecureJSON(AUTH_USER_PROFILE_KEY, profile);
        refreshRolesCache().catch(() => {});
      } catch (err) {
        if (import.meta.env.DEV) console.warn('[AuthContext] Roles refresh from User API failed:', err);
        if (!cancelled) rolesRefreshAttemptedRef.current = null;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authState, user]);

  // Refresh auth state - useful when MSAL silent auth succeeds and we need to update AuthContext
  const refreshAuthState = useCallback(async () => {
    if (isLoggingIn) {
      // Don't refresh during login process
      return;
    }

    try {
      // For MSAL, get token and user directly from MSAL provider
      // This is critical after ssoSilent() or acquireTokenSilent() succeeds
      // The token is in MSAL cache, not tokenService yet
      if (AUTH_API_CONFIG.providerType === 'msal') {
        try {
          const provider = getAuthProvider();
          if (provider instanceof MsalAuthProvider) {
            const msalToken = await provider.getAccessTokenSilently();
            if (msalToken) {
              const activeAccount = provider.getAccount();
              if (activeAccount) {
                // Extract user info from MSAL account
                const msalUser: User = {
                  id: activeAccount.localAccountId || activeAccount.homeAccountId || '',
                  email: activeAccount.username || '',
                  name: activeAccount.name || activeAccount.username || '',
                };

                // Update state immediately
                setAuthState('portal_authenticated');
                await tokenService.setAccessToken(msalToken);
                setToken(msalToken);
                setUser(msalUser);
                await setSecureJSON(AUTH_USER_KEY, msalUser);
                if (AUTH_API_CONFIG.providerType !== 'msal') {
                  scheduleTokenRefresh(msalToken);
                }
                return;
              }
            }
            // msalToken is null - silent auth failed; clear state so authState and token/user stay in sync
            setAuthState('unauthenticated');
            setToken(null);
            setRefreshTokenValue(null);
            setUser(null);
            setUserProfile(null);
            return;
          }
        } catch (msalError) {
          console.warn('[AuthContext] Failed to get token from MSAL auth provider:', msalError);
          setAuthState('unauthenticated');
          setToken(null);
          setRefreshTokenValue(null);
          setUser(null);
          setUserProfile(null);
          return;
        }
      }

      // Fallback for non-MSAL or if MSAL path fails
      // Re-check token from token service
      const currentToken = await tokenService.getAccessToken();
      const currentRefreshToken = await tokenService.getRefreshToken();
      const currentUser = await getSecureJSON<User>(AUTH_USER_KEY);
      let currentUserProfile: UserProfile | null = null;
      try {
        currentUserProfile = await getSecureJSON<UserProfile>(AUTH_USER_PROFILE_KEY);
      } catch (error) {
        console.warn('[AuthContext] Failed to retrieve user profile during refresh (optional):', error);
      }

      if (currentToken && currentUser) {
        if (tokenService.isTokenExpired(currentToken)) {
          // Token expired - try to refresh
          if (currentRefreshToken || AUTH_API_CONFIG.providerType === 'msal') {
            try {
              const response = await refreshTokenApi(currentRefreshToken || '');
              const { accessToken, refreshToken: newRefreshToken, expiresIn } = response;
              setAuthState('portal_authenticated');
              setToken(accessToken);
              setRefreshTokenValue(newRefreshToken || currentRefreshToken);
              setUser(currentUser);
              setUserProfile(currentUserProfile);
              await tokenService.setAccessToken(accessToken);
              if (newRefreshToken) {
                await tokenService.setRefreshToken(newRefreshToken);
              }
              scheduleTokenRefresh(accessToken, expiresIn);
            } catch (error) {
              console.error('[AuthContext] Token refresh failed during refreshAuthState:', error);
            }
          }
        } else {
          setAuthState('portal_authenticated');
          setToken(currentToken);
          setRefreshTokenValue(currentRefreshToken);
          setUser(currentUser);
          setUserProfile(currentUserProfile);
          scheduleTokenRefresh(currentToken);
        }
      } else {
        if (token || user) {
          setAuthState('unauthenticated');
          setToken(null);
          setRefreshTokenValue(null);
          setUser(null);
          setUserProfile(null);
        }
      }
    } catch (error) {
      console.error('[AuthContext] Failed to refresh auth state:', error);
    }
  }, [isLoggingIn, token, user, scheduleTokenRefresh]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      clearRefreshTimer();
    };
  }, [clearRefreshTimer]);

  const login = useCallback(async (accessToken: string, refreshTokenValue: string | null, newUser: User, expiresIn?: number, newUserProfile?: UserProfile | null) => {
    // Set logging in flag to prevent initializeAuth from interfering
    setIsLoggingIn(true);
    
    // Clear any existing corrupted data before setting new tokens
    // This ensures we don't have issues with old encrypted data
    try {
      await tokenService.removeTokens().catch(() => {});
      removeSecureItem(AUTH_USER_KEY);
      removeSecureItem(AUTH_USER_PROFILE_KEY);
    } catch (error) {
      console.warn('[AuthContext] Error clearing old data during login (expected):', error);
    }
    
    // Write to storage FIRST (async operations)
    await tokenService.setAccessToken(accessToken);
    
    // Only store refresh token for REST/OAuth2 providers (MSAL doesn't use refresh tokens)
    if (refreshTokenValue && AUTH_API_CONFIG.providerType !== 'msal') {
      await tokenService.setRefreshToken(refreshTokenValue);
      setRefreshTokenValue(refreshTokenValue);
    } else {
      // MSAL provider: Don't store refresh token (MSAL uses acquireTokenSilent instead)
      setRefreshTokenValue(null);
      // Note: We don't clear refresh token from storage here because removeTokens() was already called above
      // If refresh token exists in storage from a previous session, it will be ignored for MSAL
    }
    
    await setSecureJSON(AUTH_USER_KEY, newUser);
    
    // Store userProfile if provided
    if (newUserProfile) {
      await setSecureJSON(AUTH_USER_PROFILE_KEY, newUserProfile);
    }
    
    setAuthState('portal_authenticated');
    setToken(accessToken);
    setUser(newUser);
    setUserProfile(newUserProfile || null);
    
    // Only schedule token refresh for REST/OAuth2 providers
    // MSAL handles its own token refresh via acquireTokenSilent()
    if (AUTH_API_CONFIG.providerType !== 'msal') {
      scheduleTokenRefresh(accessToken, expiresIn);
    }
    
    // Clear logging in state after successful login and storage writes
    setIsLoggingIn(false);

    // Refresh roles cache on every login so it is up to date (non-fatal; roles load on demand if this fails)
    refreshRolesCache().catch((err) => {
      console.warn(
        '[AuthContext] Failed to refresh roles cache after login (login succeeded). Ensure User Service is running (VITE_API_USER_BASE_URL) and Vite proxy routes /api/v1/users to it; Auth API (VITE_AUTH_SERVICE_URL) is separate.',
        err instanceof Error ? err.message : err
      );
    });
  }, [scheduleTokenRefresh]);

  const clearAuthStateSilently = useCallback(async () => {
    const entraIdForInvalidation =
      userProfile?.entraObjectId ?? (user?.id && isEntraObjectId(user.id) ? user.id : null);
    if (entraIdForInvalidation) {
      invalidateProfileCache(entraIdForInvalidation);
    }

    setAuthState('unauthenticated');
    setToken(null);
    setRefreshTokenValue(null);
    setUser(null);
    setUserProfile(null);
    clearRefreshTimer();

    try {
      await clearPortalPersistedAuthPartial();
    } catch (error) {
      console.warn('[AuthContext] Error clearing storage silently (expected):', error);
    }
  }, [clearRefreshTimer, user, userProfile]);

  const logout = useCallback(async (options?: { terminateIdpSession?: boolean; notifyPeerPortals?: boolean }) => {
    const terminateIdpSession = options?.terminateIdpSession === true;
    const notifyPeerPortals = options?.notifyPeerPortals !== false;

    const entraIdForInvalidation =
      userProfile?.entraObjectId ?? (user?.id && isEntraObjectId(user.id) ? user.id : null);
    if (entraIdForInvalidation) {
      invalidateProfileCache(entraIdForInvalidation);
    }

    endSession('user', { invokeLogoutCallback: false });

    // Capture MSAL account before storage clears (required for logoutRedirect account hint).
    let accountForIdpLogout: import('@azure/msal-browser').AccountInfo | null = null;
    if (AUTH_API_CONFIG.providerType === 'msal' && terminateIdpSession) {
      const msalInstance = await msalReady;
      if (msalInstance) {
        accountForIdpLogout = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
      }
    }

    // Set logging out flag to prevent multiple redirects
    setIsLoggingOut(true);
    clearRefreshTimer();
    
    try {
      // Call logout API if token exists
      // Always get token from service - don't capture in closure to prevent unnecessary re-renders
      const currentToken = await tokenService.getAccessToken();
      if (currentToken) {
        try {
          await logoutApi(currentToken);
        } catch (error) {
          // Log but don't block logout
          console.error('Logout API call failed:', error);
        }
      }

      setAuthState('unauthenticated');
      setToken(null);
      setRefreshTokenValue(null);
      setUser(null);
      setUserProfile(null);

      await performPortalAuthTeardown({
        notifyPeerPortals,
        terminateIdpSession,
        accountForIdpLogout,
      });
    } finally {
      // Reset logging out flag after a short delay to allow navigation to complete
      setTimeout(() => {
        setIsLoggingOut(false);
      }, 500);
    }
  }, [clearRefreshTimer, user, userProfile]);

  // Listen for logout events from API interceptors
  useEffect(() => {
    const handleLogout = () => {
      logout();
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, [logout]);

  // CRITICAL: Set isLoggingIn getter for API interceptor
  // This allows the interceptor to check isLoggingIn state without React context
  useEffect(() => {
    setIsLoggingInGetter(() => isLoggingIn);
  }, [isLoggingIn]);

  // Register token provider for API interceptor so requests get a valid token on refresh.
  // Without this, getTokenRef is undefined and the interceptor uses getMsalInstance() which
  // can be null if state is restored from storage before msalReady resolves → 401 on users/entra.
  useEffect(() => {
    if (AUTH_API_CONFIG.providerType !== 'msal') return;
    setupApiInterceptor(async () => {
      if (tokenRef.current) return tokenRef.current;
      const msal = await msalReady;
      if (!msal) return null;
      return getValidAccessToken(msal);
    });
  }, []);

  useEffect(() => {
    if (authState === 'portal_authenticated') {
      startSession();
    }
    if (authState === 'portal_authenticated' && user) {
      markPriorPortalAuthentication();
    }
  }, [authState, user]);

  const isAuthenticated = authState === 'portal_authenticated';

  const value: AuthContextType = useMemo(() => ({
    authState,
    setAuthState,
    user,
    userProfile,
    isAuthenticated,
    isLoading,
    isLoggingOut,
    isLoggingIn,
    setIsLoggingIn,
    setLogoutInProgress: setIsLoggingOut,
    login,
    logout,
    clearAuthStateSilently,
    refreshAuthState,
    token,
    refreshToken: refreshTokenValue,
  }), [authState, setAuthState, user, userProfile, isLoading, isLoggingOut, isLoggingIn, setIsLoggingIn, login, logout, clearAuthStateSilently, refreshAuthState, refreshTokenValue]);

  return (
    <AuthContext.Provider value={value}>
      <SessionPolicyLogoutRegistrar />
      <CrossPortalLogoutListener />
      {children}
    </AuthContext.Provider>
  );
};

export { useAuth } from './authReactContext';
export type { PortalAuthState, User, AuthContextType } from './authContext.types';

/** Props for redirect guard. Use so redirects occur only inside AuthContext. */
export interface AuthRedirectGuardProps {
  to?: string;
  /** Pass-through for React Router Navigate state (e.g. { from: location }). */
  state?: Record<string, unknown>;
  replace?: boolean;
}

/**
 * Renders a redirect (default /error for unauthenticated access to protected routes).
 * Defined in AuthContext so redirects stay inside the auth module (no redirects outside AuthContext).
 */
export const AuthRedirectGuard: React.FC<AuthRedirectGuardProps> = ({
  to = '/error',
  state,
  replace = true,
}) => {
  return <Navigate to={to} state={state} replace={replace} />;
};

